import { dateLocales } from '@/components/language-switcher'
import { Contact, User } from '@/types'
import {
  differenceInMinutes,
  format,
  intervalToDuration,
  isValid,
  toDate,
} from 'date-fns'
import {
  camelCase,
  isArray,
  isNull,
  isObject,
  isString,
  isUndefined,
  mapKeys,
  mapValues,
  snakeCase,
} from 'lodash-es'
import { i18n } from 'next-i18next'

const frEUR = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
})

export const formatPrice = (price: number | undefined) => {
  if (price === undefined) {
    return null
  }

  return frEUR.format(price)
}

export const formatName = (contact?: Contact | User) => {
  if (!contact) {
    return ''
  }

  if (contact.firstName && contact.lastName) {
    return `${contact.firstName} ${contact.lastName}`
  }

  if (contact.firstName) {
    return contact.firstName
  }

  if (contact.lastName) {
    return contact.lastName
  }

  return ''
}

export const formatDate = (
  date: string | Date | null,
  toFormat: string = 'HH:mm, dd MMM yyyy'
) => {
  if (!date) {
    return ''
  }

  // date-fns v3 `format()` throws `RangeError: Invalid time value` when the date
  // is truthy but unparseable (a malformed string, or an already-Invalid Date),
  // which crashed the booking views (Sentry ALOESUITE-PROD-GC). Coerce first,
  // then validate: `isValid` rejects raw strings outright, so it must be given a
  // Date. `toDate` parses ISO strings and yields an Invalid Date for junk.
  const parsedDate = toDate(date)
  if (!isValid(parsedDate)) {
    return ''
  }

  const locale = i18n?.language ?? 'en'

  return format(parsedDate, toFormat, { locale: dateLocales[locale] })
}

const DATE_ONLY_FORMAT = 'dd MMM yyyy'

/**
 * Render a booking date-time using the operator-configured display time
 * ("Show as") for the time part when available, falling back to the raw
 * timestamp. Keeps the cart, order details and email blade in agreement.
 */
export const formatBookingDateTime = (
  startsAt: string | Date | null | undefined,
  displayTime?: string | null
) => {
  if (!startsAt) {
    return ''
  }

  if (displayTime) {
    return `${displayTime}, ${formatDate(startsAt, DATE_ONLY_FORMAT)}`
  }

  return formatDate(startsAt)
}

export const formatDuration = (
  startDate: string | Date | null,
  endDate: string | Date | null
) => {
  if (!startDate || !endDate) return ''

  const totalMinutes = differenceInMinutes(endDate, startDate)
  if (totalMinutes <= 0) return '0m'

  const days = Math.floor(totalMinutes / 1440)
  const hours = Math.floor((totalMinutes % 1440) / 60)
  const minutes = totalMinutes % 60

  const parts: string[] = []
  if (days > 0) parts.push(`${days}d`)
  if (hours > 0) parts.push(`${hours}h`)
  if (minutes > 0 || parts.length === 0) parts.push(`${minutes}m`)

  return parts.join(' ')
}

export const removeNullParams = (
  params: Record<string, any>
): URLSearchParams => {
  if (!params) {
    return new URLSearchParams()
  }

  Object.keys(params).forEach((key) => {
    const value = (params as any)[key]
    if (
      isNull(value) ||
      isUndefined(value) ||
      (isString(value) && value.length === 0)
    ) {
      delete (params as any)[key]
    }
  })

  return new URLSearchParams(params)
}

export function snakeCaseObject(obj: Record<string, any>): any {
  if (isArray(obj)) {
    return obj.map(snakeCaseObject)
  }

  if (isObject(obj)) {
    return mapKeys(mapValues(obj, snakeCaseObject), (value, key) =>
      snakeCase(key)
    )
  }

  return obj
}

export function camelCaseObject(obj: Record<string, any>): any {
  if (isArray(obj)) {
    return obj.map(camelCaseObject)
  }

  if (isObject(obj)) {
    return mapKeys(mapValues(obj, camelCaseObject), (value, key) =>
      camelCase(key)
    )
  }

  return obj
}

export const formatUrl = (url: string) => {
  if (!url) {
    return ''
  }

  if (url.startsWith('http')) {
    return url
  }

  return `https://${url}`
}

export function formatDurationFromMinutes(totalMinutes: number) {
  const duration = intervalToDuration({
    start: 0,
    end: totalMinutes * 60 * 1000,
  })

  const parts = []
  if (duration.days) parts.push(`${duration.days}d`)
  if (duration.hours) parts.push(`${duration.hours}h`)
  if (duration.minutes) parts.push(`${duration.minutes}m`)

  return parts.length > 0 ? parts.join(' ') : '0m'
}

export const supportedTimezones = ['Europe/Rome']
